import { moveItemInArray } from '@angular/cdk/drag-drop';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  mergeMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { ConnectionAdapter } from '../../adapters/connection.adapter';
import { JobsAdapter } from '../../adapters/jobs.adapter';
import { AppState } from '../../app.reducer';
import { JobsService } from '../../services/company/jobs.service';
import {
  createJob,
  createJobSuccess,
  deleteConnectionForJob,
  deleteConnectionForJobFailure,
  deleteConnectionForJobSuccess,
  deleteJobFailure,
  loadJobs,
  loadJobsCached,
  loadJobsFailure,
  loadJobsSuccess,
  sortJobs,
  sortJobsFailure,
  sortJobsSuccess,
} from './jobs.actions';
import {
  selectJobsOpenAndClosed,
  selectJobsStepsLoaded,
  selectJobsTotal,
} from './jobs.reducer';

@Injectable()
export class JobsEffects {
  private readonly actions$ = inject(Actions);
  private readonly jobsAdapter = inject(JobsAdapter);
  private readonly store = inject(Store<AppState>);
  private readonly connectionAdapter = inject(ConnectionAdapter);
  private readonly jobsService = inject(JobsService);

  // Same idea as users/facilities: skip HTTP when already loaded unless force / missing steps.
  loadJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadJobs),
      withLatestFrom(
        this.store.select(selectJobsTotal),
        this.store.select(selectJobsStepsLoaded),
      ),
      concatMap(([action, total, stepsLoaded]) => {
        const needsSteps = action.includeSteps === true && !stepsLoaded;
        const alreadyLoaded =
          total > 0 && this.jobsService.currentJobs.length > 0 && !needsSteps;

        if (!action.force && alreadyLoaded) {
          this.jobsService.finishCurrentJobsLoad();
          return of(loadJobsCached());
        }

        return this.jobsAdapter.getCurrentJobs(action.includeSteps === true).pipe(
          tap((result) => this.jobsService.applyCurrentJobsResult(result)),
          map((result) =>
            loadJobsSuccess({
              jobs: result.jobs,
              initiativeID: result.initiativeID,
              stepsLoaded: action.includeSteps === true,
            }),
          ),
          catchError((error) => of(loadJobsFailure({ error }))),
        );
      }),
    ),
  );

  createJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createJob),
      mergeMap((props) =>
        this.jobsAdapter.createJob(props.job).pipe(
          map((response) => createJobSuccess({ job: { ...props.job, id: response.id } })),
          catchError((error) => {
            return of(deleteJobFailure({ error }));
          })
        )
      )
    )
  );

  sortJobs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sortJobs),
      concatLatestFrom(() => this.store.select(selectJobsOpenAndClosed)),
      mergeMap(([props, jobs]) => {
        moveItemInArray(jobs, props.oldIndex, props.newIndex);

        return this.jobsAdapter.sortJobs(jobs.map((job) => job.id)).pipe(
          map(() => sortJobsSuccess({ jobs: jobs.map((job, index) => ({ ...job, index })) })),
          catchError((error) => {
            return of(sortJobsFailure({ error }));
          })
        );
      })
    )
  );

  /*
  updateJob$ = createEffect(() => this.actions$.pipe(
    ofType(updateJob),
    mergeMap((props) => this.jobsAdapter.editJob(props.job).pipe(
      map(() => updateJobSuccess({ job: props.job })),
      catchError(error => {
        return of(updateJobFailure({ error }))
      }),
    ))
  ));

  deleteJob$ = createEffect(() => this.actions$.pipe(
    ofType(deleteJob),
    mergeMap((props) => this.jobsAdapter.deleteJob(props.job).pipe(
      map(() => deleteJobSuccess({ id: props.job.id })),
      catchError(error => {
        return of(deleteJobFailure({ error }))
      }),
    ))
  ));
  //*/

  loadJobsFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadJobsFailure),
        tap(() => this.jobsService.finishCurrentJobsLoad()),
      ),
    { dispatch: false },
  );

  deleteConnectionForJob$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteConnectionForJob),
      mergeMap((props) =>
        this.connectionAdapter.deleteConnectionForJob(props.job.id).pipe(
          map(() => deleteConnectionForJobSuccess(props)),
          catchError((error) => {
            return of(deleteConnectionForJobFailure({ error }));
          })
        )
      )
    )
  );
}
